import React from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
    formatEntityMentionCounts,
    listKbEntities,
    searchKbEntities,
} from '../../../utils/api/kbEntitySearch';
import {isLocalEntityRef} from '../../../utils/entityLinks';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import {
    getEntityTypeChipColor,
    getEntityTypeLabel,
    isPlaceholderEntityType,
} from '../../../utils/entityTypeMeta';
import {ENTITY_CARD_DRAG_MIME} from '../tldraw/customShapes/entityCardConstants';
import {serializeEntityCardDragPayload} from '../tldraw/customShapes/entityCardDrag';
import CreateCustomEntityDialog from '../../knowledgeBase/components/CreateCustomEntityDialog';

/** Same delay as Spotlight entity search. */
const SEARCH_DEBOUNCE_MS = 350;
const SEARCH_LIMIT = 30;
const BROWSE_LIMIT = 50;

function kbHitAvatarUrl(hit) {
    const id = hit?.entityRef || hit?.id || '';
    const imageUrl = hit?.imageUrl || null;
    if (isLocalEntityRef(id)) {
        return imageUrl || `/api/entities/${encodeURIComponent(id)}/avatar`;
    }
    return resolveEntityAvatarUrl({
        wikidataId: id,
        imageUrl,
    });
}

function EntityTypeChip({entityType}) {
    const label = getEntityTypeLabel(entityType);
    if (!label || isPlaceholderEntityType(entityType)) return null;
    return (
        <Chip
            label={label}
            size="small"
            color={getEntityTypeChipColor(entityType)}
            variant="outlined"
            sx={{flexShrink: 0, height: 20, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'}}}
        />
    );
}

function CandidateChip() {
    return (
        <Chip
            label="Candidate"
            size="small"
            color="warning"
            variant="outlined"
            sx={{flexShrink: 0, height: 20, '& .MuiChip-label': {px: 0.75, fontSize: '0.68rem'}}}
        />
    );
}

function EntityMentionCounts({entity}) {
    const label = formatEntityMentionCounts(entity);
    if (!label) return null;
    return (
        <Typography
            component="span"
            variant="caption"
            data-testid="whiteboard-entity-mention-counts"
            sx={{color: 'text.disabled', flexShrink: 0, whiteSpace: 'nowrap', fontSize: 11}}
        >
            {label}
        </Typography>
    );
}

export default function WhiteboardSearchTab({onAddEntity}) {
    const [query, setQuery] = React.useState('');
    const [results, setResults] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [createOpen, setCreateOpen] = React.useState(false);

    React.useEffect(() => {
        const trimmedQuery = query.trim();
        let isCancelled = false;
        setLoading(true);

        const timer = window.setTimeout(async () => {
            try {
                if (!trimmedQuery) {
                    const hits = await listKbEntities(BROWSE_LIMIT);
                    if (!isCancelled) {
                        setResults(Array.isArray(hits) ? hits : []);
                    }
                    return;
                }

                const {results: hits} = await searchKbEntities(trimmedQuery, SEARCH_LIMIT);
                if (!isCancelled) {
                    setResults(Array.isArray(hits) ? hits : []);
                }
            } catch (error) {
                if (isCancelled) return;
                console.error('Failed to search entities:', error);
                setResults([]);
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        }, trimmedQuery ? SEARCH_DEBOUNCE_MS : 0);

        return () => {
            isCancelled = true;
            window.clearTimeout(timer);
        };
    }, [query]);

    const handleDragStart = React.useCallback((event, entity) => {
        const payload = serializeEntityCardDragPayload(entity);
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData(ENTITY_CARD_DRAG_MIME, payload);
    }, []);

    const handleCustomEntityCreated = React.useCallback((entity) => {
        onAddEntity?.(entity);
    }, [onAddEntity]);

    const trimmedQuery = query.trim();
    const isSearch = Boolean(trimmedQuery);
    const showCreateButton = isSearch && trimmedQuery.length >= 2 && !loading && !results.length;

    return (
        <Stack height="100%" minHeight={0} overflow="hidden">
            <Box sx={{p: 2, pb: 1.5}}>
                <Stack spacing={1.25}>
                    {showCreateButton ? (
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon/>}
                            onClick={() => setCreateOpen(true)}
                        >
                            Create
                        </Button>
                    ) : null}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search entities…"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small"/>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Stack>
            </Box>

            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 1}}>
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <CircularProgress size={28}/>
                    </Box>
                ) : null}

                {!loading && isSearch && !results.length ? (
                    <Box sx={{px: 2, py: 1}}>
                        <Typography variant="body2" color="text.secondary">
                            No entities found.
                        </Typography>
                    </Box>
                ) : null}

                {!loading && !isSearch && !results.length ? (
                    <Box sx={{px: 2, py: 1}}>
                        <Typography variant="body2" color="text.secondary">
                            No Knowledge Base entities yet. Type to search, or create a custom entity.
                        </Typography>
                    </Box>
                ) : null}

                {!loading && results.length ? (
                    <List disablePadding>
                        {results.map((entity) => {
                            const entityRef = entity.entityRef || entity.id;
                            const name = entity.canonicalName || entity.label || entityRef;
                            const avatarUrl = kbHitAvatarUrl(entity);
                            const isDraftCandidate = entity.draftCandidate === true;

                            return (
                                <ListItem key={entityRef} disablePadding sx={{mb: 0.5}}>
                                    <ListItemButton
                                        draggable
                                        onDragStart={(event) => handleDragStart(event, entity)}
                                        data-testid="whiteboard-search-entity-row"
                                        sx={{
                                            alignItems: 'flex-start',
                                            borderRadius: 1,
                                            border: '1px solid rgba(15, 23, 42, 0.12)',
                                            bgcolor: '#ffffff',
                                            px: 1.5,
                                            py: 1,
                                            gap: 1.25,
                                        }}
                                    >
                                        <Avatar
                                            src={avatarUrl || undefined}
                                            alt=""
                                            data-testid="whiteboard-search-entity-avatar"
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                fontSize: 14,
                                                flexShrink: 0,
                                                bgcolor: 'action.selected',
                                            }}
                                        >
                                            {String(name || '?').slice(0, 1).toUpperCase()}
                                        </Avatar>

                                        <Stack sx={{flex: 1, minWidth: 0}} spacing={0.25}>
                                            <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
                                                <Typography
                                                    variant="subtitle2"
                                                    noWrap
                                                    title={name}
                                                    sx={{fontWeight: 600, color: '#0f172a', flex: 1, minWidth: 0}}
                                                >
                                                    {name}
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.75}
                                                    alignItems="center"
                                                    sx={{flexShrink: 0, ml: 'auto'}}
                                                >
                                                    {isDraftCandidate
                                                        ? <CandidateChip/>
                                                        : <EntityTypeChip entityType={entity.entityType}/>}
                                                    <EntityMentionCounts entity={entity}/>
                                                </Stack>
                                            </Stack>
                                            <Stack direction="row" alignItems="flex-end" spacing={0.5} minWidth={0}>
                                                {entity.description ? (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        data-testid="whiteboard-search-entity-description"
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            display: 'block',
                                                            whiteSpace: 'normal',
                                                            overflow: 'visible',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {entity.description}
                                                    </Typography>
                                                ) : (
                                                    <Box sx={{flex: 1, minWidth: 0}}/>
                                                )}
                                                <Tooltip title="Add to chart">
                                                    <IconButton
                                                        size="small"
                                                        className="whiteboard-entity-add-button"
                                                        aria-label="Add to chart"
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            onAddEntity?.(entity);
                                                        }}
                                                        sx={{flexShrink: 0, p: 0.25, mb: -0.25}}
                                                    >
                                                        <AddCircleOutlineIcon sx={{fontSize: 18}}/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                ) : null}
            </Box>

            <CreateCustomEntityDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onSuccess={handleCustomEntityCreated}
                initialName={trimmedQuery}
            />
        </Stack>
    );
}
